/**
 * @mention parsing — pure.
 *
 * Given a comment body and a roster of candidate names, return the names
 * actually @mentioned in the text. Honest by construction: it only reports
 * a mention when `@Name` genuinely appears in the body — nothing is
 * inferred or fabricated. Matching is case-insensitive and prefers the
 * longest candidate at a given position so "@Grant Walker" wins over
 * "@Grant" when both are candidates.
 */

/** Normalize for comparison (case-insensitive, collapse internal spaces). */
function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Return the subset of `candidates` that are @mentioned in `body`.
 * Order follows `candidates`; duplicates are removed.
 */
export function parseMentions(body: string, candidates: readonly string[]): string[] {
  if (!body.includes('@')) return [];
  const hay = norm(body);
  const seen = new Set<string>();
  const out: string[] = [];
  // Longest-first so a longer name isn't masked by a shorter prefix match.
  const ordered = [...candidates].sort((a, b) => b.length - a.length);
  for (const name of ordered) {
    const token = `@${norm(name)}`;
    if (token.length > 1 && hay.includes(token) && !seen.has(name)) {
      seen.add(name);
    }
  }
  // Emit in the caller's original candidate order for stable output.
  for (const name of candidates) if (seen.has(name)) out.push(name);
  return out;
}
